import {
  Avatar,
  Box,
  Button,
  Drawer,
  List,
  ListItemButton,
  styled,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import CompanyAPI from "apis/CompanyAPI";
import useSettings from "hooks/useSettings";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ScrollBar from "simplebar-react";
import { THEMES } from "utils/constants";
import topMenuList from "./customerTopMenuList";
import { MenuItem } from "./topMenuList";

interface SideNavBarProps {
  showMobileSideBar: boolean;
  closeMobileSideBar: () => void;
}

const MainMenu = styled(Box, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded: boolean }>(({ theme, expanded }) => ({
  left: 0,
  width: expanded ? 240 : 88,
  height: "100%",
  position: "fixed",
  boxShadow: theme.shadows[2],
  transition: "width 0.25s ease",
  zIndex: theme.zIndex.drawer + 11,
  backgroundColor: theme.palette.background.paper,
  borderRight: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down("md")]: { left: -88 },
  "& .simplebar-track.simplebar-vertical": { width: 6 },
  "& .simplebar-scrollbar:before": {
    background: theme.palette.text.secondary,
  },
}));

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded: boolean }>(({ expanded, theme }) => ({
  marginBottom: "0.4rem",
  justifyContent: expanded ? "flex-start" : "center",
  paddingLeft: expanded ? "0.9rem" : "0.5rem",
  marginInline: "0.5rem",
  borderRadius: 12,
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const CustomerDashboardSidebar: FC<SideNavBarProps> = ({
  showMobileSideBar,
  closeMobileSideBar,
}) => {
  const navigate = useNavigate();
  const [active, setActive] = useState("Dashboard");
  const [expanded] = useState(true);
  const downMd = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const { settings, saveSettings } = useSettings();
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);

  useEffect(() => {
    let imageUrl: string;

    CompanyAPI.getLogo()
      .then((response) => {
        if (response.size > 0) {
          return response;
        }

        throw new Error("Failed to load logo");
      })
      .then((blob) => {
        imageUrl = URL.createObjectURL(blob);
        setCompanyLogo(imageUrl);
      })
      .catch(() => {
        setCompanyLogo("/static/avatar/001-man.svg");
      });

    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, []);

  const handleActiveMainMenu = (menuItem: MenuItem) => () => {
    setActive(menuItem.title);
    navigate(menuItem.path);
    closeMobileSideBar();
  };

  const goToHome = () => navigate("/customer-access");
  const isDarkMode = settings.theme === THEMES.DARK;

  const handleToggleTheme = () => {
    saveSettings({
      ...settings,
      theme: isDarkMode ? THEMES.LIGHT : THEMES.DARK,
    });
  };

  const mainSideBarContent = (
    <List sx={{ height: "100%", p: 1 }}>
      <StyledListItemButton
        disableRipple
        expanded={expanded}
        onClick={goToHome}
        sx={{ flexDirection: "column", alignItems: "center", py: 2.5 }}
      >
        <Avatar
          src={companyLogo || undefined}
          alt="User"
          sx={{ width: 72, height: 72, mb: 1 }}
        />
        {expanded ? (
          <Typography variant="body2" color="text.secondary">
            Portal do Cliente
          </Typography>
        ) : null}
      </StyledListItemButton>

      <ScrollBar style={{ maxHeight: "calc(100% - 128px)" }}>
        {topMenuList.map((nav, index) => (
          <Tooltip
            title={!expanded ? nav.title : ""}
            placement="right"
            key={index}
          >
            <StyledListItemButton
              disableRipple
              onClick={handleActiveMainMenu(nav)}
              expanded={expanded}
              sx={{
                backgroundColor:
                  active === nav.title ? "action.hover" : "transparent",
              }}
            >
              <nav.Icon
                sx={{
                  color:
                    active === nav.title ? "primary.main" : "text.secondary",
                }}
              />
              {expanded ? (
                <Typography sx={{ ml: 1 }}>{nav.title}</Typography>
              ) : null}
            </StyledListItemButton>
          </Tooltip>
        ))}
      </ScrollBar>

      <Box px={1.3} pb={1}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={
            isDarkMode ? <LightModeRoundedIcon /> : <DarkModeRoundedIcon />
          }
          onClick={handleToggleTheme}
        >
          {isDarkMode ? "Tema claro" : "Tema escuro"}
        </Button>
      </Box>
    </List>
  );

  if (downMd) {
    return (
      <Drawer
        anchor="left"
        open={showMobileSideBar}
        onClose={closeMobileSideBar}
        PaperProps={{ sx: { width: expanded ? 240 : 88 } }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            width: "inherit",
            position: "fixed",
            overflow: "hidden",
            flexDirection: "column",
            boxShadow: (theme) => theme.shadows[1],
            backgroundColor: (theme) => theme.palette.background.paper,
            borderRight: (theme) => `1px solid ${theme.palette.divider}`,
            "& .simplebar-track.simplebar-vertical": { width: 6 },
            "& .simplebar-scrollbar:before": {
              background: (theme) => theme.palette.text.secondary,
            },
          }}
        >
          {mainSideBarContent}
        </Box>
      </Drawer>
    );
  }

  return <MainMenu expanded={expanded}>{mainSideBarContent}</MainMenu>;
};

export default CustomerDashboardSidebar;
